/**
 * TrainingRecommendationCard
 * 
 * Enhanced training artifact card with Phase 0 guardrails:
 * - Provider attribution (external link)
 * - Free/Paid badge with clear styling
 * - "Why you're seeing this" explanation
 * - Format + duration display
 * - Save / Assign / Dismiss actions
 * - All links open externally (never embed)
 * 
 * For books:
 * - "Buy from {seller}" button with purchase_url
 * - "Reference material" badge for reference-only books
 */

import { useState, useCallback } from 'react';
import { CapabilityAdvisoryPanel } from '@/components/training/CapabilityAdvisoryPanel';
import { PaidTrainingSoftGateModal } from '@/components/training/PaidTrainingSoftGateModal';
import { useArtifactCapabilityAdvisory } from '@/hooks/useArtifactCapabilityAdvisory';
import { Badge } from '@/components/ui/badge';
import { AiDraftSummaryNotice } from '@/components/training/AiDraftSummaryNotice';
import { Button } from '@/components/ui/button';
import {
  ExternalLink,
  Clock,
  CheckCircle2,
  Bookmark,
  UserPlus,
  X,
  DollarSign,
  Sparkles,
  Calendar,
  ShoppingCart,
  BookOpen,
} from 'lucide-react';
import { useOpenTrainingWithSession } from '@/hooks/useOpenTrainingWithSession';
import { MobileTrainingFallbackLaunchInterstitial } from '@/components/training/MobileTrainingFallbackLaunchInterstitial';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ARTIFACT_TYPE_LABELS,
  ARTIFACT_TYPE_ICONS,
  ACCESS_TYPE_LABELS,
  SKILL_DEPTH_LABELS,
  type LearningIntent,
  type TrainingArtifact,
} from '@/types/training';
import type { PassportRecommendationDisplayState } from '@/utils/training/passportRecommendationDisplayState';

// Extended artifact type with provider info
interface ArtifactWithProvider extends TrainingArtifact {
  provider?: {
    id: string;
    name: string;
    display_name: string;
    website_url?: string;
  } | null;
  whyShowing?: string;
}

interface TrainingRecommendationCardProps {
  artifact: ArtifactWithProvider;
  whyShowing?: string;
  onOpen: (intent?: LearningIntent) => void;
  onSave?: () => void;
  onAssign?: () => void;
  onDismiss?: () => void;
  showIntentSelector?: boolean;
  showActions?: boolean;
  compact?: boolean;
  // Phase 2: Next offering info
  nextOffering?: { date: string; registrationUrl: string } | null;
  // Consumption tracking
  technicianProfileId?: string;
  surface?: 'foundational' | 'browse';
  // Access control: only owner can open training
  canOpen?: boolean;
  // Batch 18-G19 — optional Passport display state. When provided,
  // `displayState.reasonLabel` drives the "Why you're seeing this"
  // line and `displayState.reasonFragment` renders a small second
  // line below it. The G14 / G16 policy + evidence badge families
  // are NOT used here (Passport optional recommendations are not
  // assigned requirements). Helper details:
  // `src/utils/training/passportRecommendationDisplayState.ts`.
  displayState?: PassportRecommendationDisplayState;
}

export function TrainingRecommendationCard({
  artifact,
  whyShowing,
  onOpen,
  onSave,
  onAssign,
  onDismiss,
  showIntentSelector = true,
  showActions = true,
  compact = false,
  nextOffering,
  technicianProfileId,
  surface = 'foundational',
  canOpen = true,
  displayState,
}: TrainingRecommendationCardProps) {
  const [intent, setIntent] = useState<LearningIntent | ''>('');
  const [showSoftGate, setShowSoftGate] = useState(false);
  // Batch 18-F11.9: opt-in mobile fallback affordance. Strictly UI
  // state — toggling does NOT call openTraining, does NOT call
  // window.open, does NOT create a session row. The user must click
  // the interstitial's own Open button to launch, at which point
  // onOpenProvider delegates back to `handleOpenWithTracking` (the
  // same code path the primary "Open" button uses), so there is
  // exactly one session row + one window.open per launch.
  const [showMobileFallback, setShowMobileFallback] = useState(false);
  const { openTraining } = useOpenTrainingWithSession();
  const { advisoryEntries } = useArtifactCapabilityAdvisory(artifact.id);

  // Determine target URL for this artifact
  const getTargetUrl = useCallback(() => {
    const isBook = artifact.artifact_type === 'book';
    const hasPurchaseUrl = isBook && artifact.purchase_url && artifact.purchase_provider_name;
    
    if (isBook && hasPurchaseUrl) {
      return artifact.purchase_url!;
    }
    return artifact.access_url || '#';
  }, [artifact]);

  // Handle artifact open with consumption tracking
  // CRITICAL: window.open happens FIRST inside openTraining to avoid popup blocking
  const handleOpenWithTracking = useCallback(() => {
    const targetUrl = getTargetUrl();
    
    // Use the safe open pattern: window opens immediately, then fire-and-forget logging
    if (technicianProfileId && (surface === 'foundational' || surface === 'browse')) {
      openTraining({
        technicianProfileId,
        artifactId: artifact.id,
        artifactType: artifact.artifact_type,
        expectedDurationMinutes: artifact.estimated_time_minutes || null,
        surface,
        source: 'learning_center',
        targetUrl,
        launchSource: 'passport_recommendation_card',
        intent: intent || undefined,
      });
    } else {
      // Fallback: just open window if no tracking needed
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
    
    // Call original onOpen handler for UI updates
    onOpen(intent || undefined);
  }, [technicianProfileId, surface, artifact, intent, onOpen, openTraining, getTargetUrl]);

  // Level 2 soft gate intercept for paid training with capability entries
  const handleActionClick = useCallback(() => {
    const artifactIsPaid = artifact.access_type !== 'free';
    if (artifactIsPaid && advisoryEntries.length > 0) {
      setShowSoftGate(true);
    } else {
      handleOpenWithTracking();
    }
  }, [artifact.access_type, advisoryEntries.length, handleOpenWithTracking]);

  const isPaid = artifact.access_type !== 'free';
  const providerDisplayName = artifact.provider?.display_name || artifact.provider_name;
  // Batch 18-G19 — the G18-recommended Passport display adapter
  // takes precedence over the raw `whyShowing` string. When the
  // parent does not pass a `displayState` (e.g. the Browse surface
  // or any third-party caller), the existing `whyShowing` /
  // `artifact.whyShowing` / "Recommended for you" fallback chain
  // still applies.
  const explanation =
    displayState?.reasonLabel ?? whyShowing ?? artifact.whyShowing ?? 'Recommended for you';
  
  // Book-specific checks
  const isBook = artifact.artifact_type === 'book';
  const isReferenceOnly = isBook && artifact.is_reference_only;
  const hasPurchaseUrl = isBook && artifact.purchase_url && artifact.purchase_provider_name;
  
  // Format next offering date if available
  const formattedNextSession = nextOffering ? (() => {
    try {
      const date = new Date(nextOffering.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  })() : null;

  // Render the primary action button based on artifact type
  // CRITICAL: Using onClick with window.open inside handler (no <a> tags)
  // This ensures popup-safe opening with tracking
  const renderActionButton = () => {
    // Access control: if canOpen is false, show "View Only" badge
    if (canOpen === false) {
      return (
        <Badge variant="outline" className="text-xs text-muted-foreground ml-1">
          View Only
        </Badge>
      );
    }

    if (isBook) {
      if (isReferenceOnly) {
        // Reference-only books - no action button, show badge instead
        return null;
      }
      
      if (hasPurchaseUrl) {
        // Book with purchase URL - show "Buy from {seller}" button
        // Using handleActionClick for soft gate intercept
        return (
          <Button 
            size="sm" 
            onClick={handleActionClick}
            className="gap-1 ml-1"
          >
            <ShoppingCart className="h-3 w-3" />
            Buy from {artifact.purchase_provider_name}
            <ExternalLink className="h-3 w-3" />
          </Button>
        );
      }
      
      // Book without purchase info
      return (
        <Badge variant="outline" className="text-xs text-muted-foreground">
          Purchase info unavailable
        </Badge>
      );
    }
    
    // Default for video/course - handleActionClick for soft gate intercept
    return (
      <Button 
        size="sm" 
        onClick={handleActionClick}
        className="gap-1 ml-1"
      >
        Open
        <ExternalLink className="h-3 w-3" />
      </Button>
    );
  };

  return (
    <>
    <div className={cn(
      "p-4 rounded-lg border bg-card hover:shadow-md transition-shadow group",
      compact && "p-3"
    )}>
      {/* Provider attribution + Why showing */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{providerDisplayName}</span>
          {artifact.provider?.website_url && (
            <a
              href={artifact.provider.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        
        {/* Free/Paid Badge - prominent styling */}
        {isReferenceOnly ? (
          <Badge 
            variant="outline"
            className="text-xs font-medium bg-slate-50 text-slate-600 border-slate-300"
          >
            <BookOpen className="h-3 w-3 mr-1" />
            Reference Only
          </Badge>
        ) : (
          <Badge 
            variant={isPaid ? 'outline' : 'default'}
            className={cn(
              "text-xs font-medium",
              isPaid 
                ? "border-amber-500 text-amber-600 dark:text-amber-400" 
                : "bg-green-600 hover:bg-green-600"
            )}
          >
            {isPaid ? (
              <>
                <DollarSign className="h-3 w-3 mr-0.5" />
                {ACCESS_TYPE_LABELS[artifact.access_type]}
              </>
            ) : (
              'Free'
            )}
          </Badge>
        )}
      </div>

      {/* Main content */}
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0">
          {ARTIFACT_TYPE_ICONS[artifact.artifact_type]}
        </span>
        
        <div className="min-w-0 flex-1">
          <h4 className={cn("font-semibold leading-tight", compact ? "text-sm" : "text-base")}>
            {artifact.title}
          </h4>
          
          {artifact.instructor_name && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {artifact.instructor_name}
            </p>
          )}
          
          {/* Badges row */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            <Badge variant="secondary" className="text-xs">
              {ARTIFACT_TYPE_LABELS[artifact.artifact_type]}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {SKILL_DEPTH_LABELS[artifact.skill_depth]}
            </Badge>
            {/* Batch 18-G19 — content-quality chip renamed from
                "Verified" to "OptiBay reviewed content" so it does
                not collide with the G16 `Verified by manager`
                evidence label on `LearningRequirementsTab`. The
                Passport concept is "we reviewed this artifact",
                NOT "a manager attested the technician completed
                it". The chip itself is still driven by the raw
                `artifact.confidence_level === 'optibay_verified'`
                flag — the G19 helper's `displayBadges` array also
                emits the same chip for callers that thread the
                display state through, so consumers can drive the
                chip from either path. */}
            {artifact.confidence_level === 'optibay_verified' && (
              <Badge
                variant="default"
                className="text-xs bg-primary"
                data-testid="passport-recommendation-badge-optibay-reviewed-content"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" />
                OptiBay reviewed content
              </Badge>
            )}
          </div>

          {/* Duration + format info + next session */}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
            {artifact.estimated_time_minutes && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {artifact.estimated_time_minutes < 60 
                  ? `${artifact.estimated_time_minutes} min`
                  : `${Math.round(artifact.estimated_time_minutes / 60 * 10) / 10} hrs`
                }
              </span>
            )}
            {artifact.delivery_format === 'live' && (
              <span className="text-amber-600 dark:text-amber-400">Live</span>
            )}
            {/* Phase 2: Next session badge */}
            {formattedNextSession && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                <Calendar className="h-3 w-3 mr-1" />
                Next: {formattedNextSession}
              </Badge>
            )}
            {artifact.travel_required && (
              <span className="text-muted-foreground">Travel Required</span>
            )}
          </div>

          {/* Reference-only notice for books */}
          {isReferenceOnly && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50 rounded px-2 py-1.5">
              <BookOpen className="h-3 w-3 flex-shrink-0" />
              <span>📚 Reference material – not publicly sold</span>
            </div>
          )}

          {/* Why you're seeing this */}
          {!isReferenceOnly && (
            <div
              className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5"
              data-testid="passport-recommendation-reason-label"
            >
              <Sparkles className="h-3 w-3 mt-0.5 flex-shrink-0 text-primary" />
              <span>{explanation}</span>
            </div>
          )}
          {/* Batch 18-G19 — optional second reason line. Only
              renders when the G19 helper finds a second recognized
              clause in the multi-clause `whyShowing` string. */}
          {!isReferenceOnly && displayState?.reasonFragment && (
            <p
              className="mt-1 pl-5 text-xs text-muted-foreground"
              data-testid="passport-recommendation-reason-fragment"
            >
              {displayState.reasonFragment}
            </p>
          )}

          {/* Original summary if available */}
          {artifact.original_summary_short && (
            <>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                {artifact.original_summary_short}
              </p>
              {/* TRN-1: warn when the summary is an unreviewed AI draft. */}
              <AiDraftSummaryNotice summarySource={artifact.summary_source} />
            </>
          )}

          {/* Level 1 Advisory — compact chip row (free + paid, never blocking) */}
          <CapabilityAdvisoryPanel artifactId={artifact.id} variant="compact" />
        </div>
      </div>

      {/* Actions row */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t">
        <div className="flex items-center gap-2">
          {showIntentSelector && !compact && !isReferenceOnly && (
            <Select value={intent} onValueChange={(v) => setIntent(v as LearningIntent | '')}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue placeholder="Why opening?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="curiosity">Curiosity</SelectItem>
                <SelectItem value="job_help">Job Help</SelectItem>
                <SelectItem value="skill_growth">Skill Growth</SelectItem>
                <SelectItem value="required">Required</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex items-center gap-1">
          {showActions && (
            <>
              {onSave && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => { e.stopPropagation(); onSave(); }}
                      >
                        <Bookmark className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Save for later</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {onAssign && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => { e.stopPropagation(); onAssign(); }}
                      >
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Assign to tech</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {onDismiss && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Dismiss</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </>
          )}
          
          {renderActionButton()}
        </div>
      </div>

      {/* Batch 18-F11.9: opt-in mobile fallback affordance.
          Scoped to the "Open training" branch only — books (buy /
          reference-only / unavailable) and the View-Only state don't
          fit the interstitial's "Open training" copy. The toggle is
          a secondary ghost-variant button below the actions row;
          clicking it never auto-opens the provider URL and never
          creates a session row. The launch only happens when the
          user clicks the interstitial's own Open button, which
          delegates back to handleOpenWithTracking — the same path
          the primary "Open" button uses. */}
      {canOpen !== false && !isBook && artifact.access_url && (
        <div className="mt-3 pt-3 border-t space-y-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs h-7"
            onClick={() => setShowMobileFallback((v) => !v)}
            data-testid="passport-recommendation-mobile-fallback-toggle"
          >
            {showMobileFallback
              ? 'Hide mobile fallback'
              : 'Open with mobile fallback'}
          </Button>
          {showMobileFallback && (
            <MobileTrainingFallbackLaunchInterstitial
              trainingTitle={artifact.title}
              providerName={providerDisplayName}
              providerUrl={getTargetUrl()}
              estimatedDurationMinutes={
                artifact.estimated_time_minutes ?? null
              }
              onOpenProvider={() => handleOpenWithTracking()}
              onCancel={() => setShowMobileFallback(false)}
            />
          )}
        </div>
      )}
    </div>
    <PaidTrainingSoftGateModal
      open={showSoftGate}
      onOpenChange={setShowSoftGate}
      artifact={artifact}
      advisoryEntries={advisoryEntries}
      onProceed={() => { setShowSoftGate(false); handleOpenWithTracking(); }}
      onViewPrep={() => setShowSoftGate(false)}
    />
  </>
  );
}
